/**
 * Bottom HUD while navigating: ETA / remaining, speed readout, recenter and
 * end buttons, plus transient status toasts (rerouting, offline).
 */

import type { NavProgress } from '../core/types';
import { formatDistance, formatDuration, formatEta, formatSpeed, speedUnitLabel } from '../core/format';
import type { UnitSystem } from '../core/settings';
import { t } from '../core/i18n';

export class NavHud {
  readonly el: HTMLElement;
  private recenterEl: HTMLButtonElement;
  private toastTimer: number | null = null;

  constructor(
    parent: HTMLElement,
    private actions: { onEnd: () => void; onRecenter: () => void; onOverview: () => void },
  ) {
    this.el = document.createElement('div');
    this.el.className = 'hud';
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="hud__toast" hidden></div>
      <div class="hud__bar">
        <button class="hud__end" aria-label="End navigation">
          <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>
        </button>
        <div class="hud__stats" role="button" tabindex="0" aria-label="Route overview">
          <div class="hud__eta">--:--</div>
          <div class="hud__sub"><span class="hud__dist">--</span> · <span class="hud__dur">--</span></div>
        </div>
        <div class="hud__speed">
          <div class="hud__speed-value">--</div>
          <div class="hud__speed-unit">km/h</div>
        </div>
      </div>`;
    parent.appendChild(this.el);

    /*
     * Re-centre lives OUTSIDE the HUD stack.
     *
     * Inside it, it sat directly under the Spotify strip - which docks above
     * the HUD during navigation and is painted over it. A round GPS button
     * beside the strip is both visible and balances the bottom of the screen,
     * which is otherwise all weight on the left.
     */
    this.recenterEl = document.createElement('button');
    this.recenterEl.className = 'hud__recenter';
    this.recenterEl.hidden = true;
    this.recenterEl.setAttribute('aria-label', t('nav.recenter'));
    this.recenterEl.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="6.2" fill="none" stroke="currentColor" stroke-width="2"/>
        <circle cx="12" cy="12" r="2.1" fill="currentColor"/>
        <path d="M12 1.6 V5 M12 19 v3.4 M1.6 12 H5 M19 12 h3.4" stroke="currentColor"
              stroke-width="2" stroke-linecap="round"/>
      </svg>`;
    parent.appendChild(this.recenterEl);
    this.recenterEl.addEventListener('click', () => this.actions.onRecenter());

    this.el.querySelector('.hud__end')!.addEventListener('click', () => this.actions.onEnd());
    this.el.querySelector('.hud__stats')!.addEventListener('click', () => this.actions.onOverview());
  }

  show(): void {
    this.el.hidden = false;
  }

  hide(): void {
    this.el.hidden = true;
    this.setRecenterVisible(false);
  }

  update(progress: NavProgress, speedMs: number | null, units: UnitSystem, speedWarn: boolean): void {
    (this.el.querySelector('.hud__eta') as HTMLElement).textContent = formatEta(progress.durationRemaining);
    (this.el.querySelector('.hud__dist') as HTMLElement).textContent = formatDistance(progress.distanceRemaining, units);
    (this.el.querySelector('.hud__dur') as HTMLElement).textContent = formatDuration(progress.durationRemaining);
    (this.el.querySelector('.hud__speed-value') as HTMLElement).textContent = formatSpeed(speedMs, units);
    (this.el.querySelector('.hud__speed-unit') as HTMLElement).textContent = speedUnitLabel(units);
    this.el.querySelector('.hud__speed')!.classList.toggle('is-over', speedWarn);
  }

  setRecenterVisible(visible: boolean): void {
    this.recenterEl.hidden = !visible;
  }

  /**
   * Transient status line. `action` adds a single tappable button - used by the
   * fuel detour so a mis-tap is one tap to reverse rather than a re-route.
   */
  toast(
    message: string,
    sticky = false,
    action?: { label: string; onAction: () => void },
  ): void {
    const el = this.el.querySelector('.hud__toast') as HTMLElement;
    el.textContent = message;
    el.hidden = false;

    if (action) {
      const btn = document.createElement('button');
      btn.className = 'hud__toast-action';
      btn.textContent = action.label;
      btn.addEventListener('click', () => {
        action.onAction();
        el.hidden = true;
      });
      el.appendChild(btn);
    }

    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    if (!sticky) {
      this.toastTimer = window.setTimeout(() => (el.hidden = true), action ? 10_000 : 4000);
    }
  }

  clearToast(): void {
    (this.el.querySelector('.hud__toast') as HTMLElement).hidden = true;
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
  }
}
